import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Briefcase, GitBranch, Mail } from "lucide-react";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { GlassCard } from "@/components/GlassCard";
import { StatusBadge } from "@/components/Badges";
import api from "@/lib/api";
import { goldBtnSm, glassBtn, TIERS } from "@/lib/constants";

function Stat({ icon: Icon, label, value }) {
  return (
    <GlassCard data-testid="admin-stat-tile" className="p-5">
      <div className="flex items-center justify-between">
        <div className="pdg-kicker">{label}</div>
        <Icon size={16} className="text-[hsl(var(--pdg-gold))]" strokeWidth={1.6} />
      </div>
      <div className="mt-3 pdg-serif text-3xl text-foreground">{value}</div>
    </GlassCard>
  );
}

export default function AdminHome() {
  const [ov, setOv] = useState(null);
  const [apps, setApps] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [tierSel, setTierSel] = useState({});

  const load = async () => {
    const [o, a, r] = await Promise.all([
      api.get("/admin/overview"), api.get("/admin/applications?status=pending"), api.get("/admin/deal-access-requests"),
    ]);
    setOv(o.data); setApps(a.data); setReqs(r.data);
  };
  useEffect(() => { load(); }, []);

  const verify = async (id) => {
    try { await api.post(`/admin/applications/${id}/verify`, { tier: tierSel[id] || "principal" }); toast.success("Investor verified."); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const decline = async (id) => {
    try { await api.post(`/admin/applications/${id}/decline`, { reason: "" }); toast.success("Application declined."); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const grant = async (req) => {
    try { await api.post("/admin/deal-access/grant", { investor_id: req.investor_id, deal_id: req.deal_id, request_id: req.id }); toast.success("Access granted."); load(); }
    catch (e) { toast.error("Failed"); }
  };

  if (!ov) return <PortalLayout title="Command Centre"><p className="text-sm text-muted-foreground">Loading…</p></PortalLayout>;

  return (
    <PortalLayout title="Command Centre">
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Users} label="Applications pending" value={ov.applications_pending} />
        <Stat icon={Users} label="Verified investors" value={ov.investors_verified} />
        <Stat icon={Briefcase} label="Deals in pipeline" value={ov.deals_pipeline} />
        <Stat icon={GitBranch} label="Events queued" value={ov.integration_events_queued} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2 mt-6">
        <GlassCard className="p-6">
          <h2 className="pdg-serif text-2xl text-foreground mb-4">Pending applications</h2>
          <div className="space-y-3">
            {apps.length === 0 && <p className="text-sm text-muted-foreground">No pending applications.</p>}
            {apps.map((a) => (
              <div key={a.id} data-testid="admin-application-row" className="p-4 rounded-xl border border-border">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm text-foreground">{a.full_name}</div>
                    <div className="text-xs text-muted-foreground">{a.organisation_name} &middot; {a.country_of_incorporation}</div>
                    <div className="text-xs text-muted-foreground mt-1">{a.email} &middot; {a.email_confirmed ? "email confirmed" : "email unconfirmed"}</div>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <select value={tierSel[a.id] || "principal"} onChange={(e) => setTierSel({ ...tierSel, [a.id]: e.target.value })}
                    className="h-9 rounded-lg bg-[hsl(var(--pdg-charcoal))] border border-border text-xs px-2 text-foreground">
                    {Object.entries(TIERS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <button onClick={() => verify(a.id)} data-testid={`admin-verify-${a.id}`} className={goldBtnSm}>Verify</button>
                  <button onClick={() => decline(a.id)} data-testid={`admin-decline-${a.id}`} className={glassBtn + " h-9 px-3 text-xs"}>Decline</button>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <h2 className="pdg-serif text-2xl text-foreground mb-4">Deal access requests</h2>
          <div className="space-y-3">
            {reqs.length === 0 && <p className="text-sm text-muted-foreground">No pending access requests.</p>}
            {reqs.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 p-4 rounded-xl border border-border">
                <div>
                  <div className="text-sm text-foreground">{r.deal_title}</div>
                  <div className="text-xs text-muted-foreground">{r.investor_email}</div>
                </div>
                <button onClick={() => grant(r)} data-testid={`admin-grant-${r.id}`} className={goldBtnSm}>Grant</button>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      <GlassCard className="p-6 mt-6">
        <h2 className="pdg-serif text-2xl text-foreground mb-4">Recent activity</h2>
        <div className="space-y-2">
          {(ov.recent_activity || []).map((a) => (
            <div key={a.id} className="flex items-center justify-between text-sm py-2 border-b border-border">
              <span className="text-foreground/85">{a.action}</span>
              <span className="text-xs text-muted-foreground pdg-mono">{a.user_email || a.ip} &middot; {new Date(a.timestamp).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </GlassCard>
    </PortalLayout>
  );
}
